import { ReactElement } from 'react';
import { useIntl } from 'react-intl';

import { Button } from '@calm-web/design-system';

import GuideList from '@/components/app/program/GuideList';
import Narrator from '@/components/app/program/Narrator';
import { useGuidePlay } from '@/hooks/app/useGuidePlay';

import messages from './messages';
import { ButtonWrapper, Description, Title, TrailerButton, Wrapper } from './styles';
import { AltProgramLayoutProps } from './types';

const AltProgramLayout = ({ program, onPlay: onPlayOverride }: AltProgramLayoutProps): ReactElement => {
	const { formatMessage } = useIntl();
	const onGuidePlay = useGuidePlay();

	const getTrailer = () => {
		const trailerGuides = program?.guides.filter(guide => guide.is_trailer);
		if (!trailerGuides) return null;

		return trailerGuides[0];
	};

	const hasTrailer = Boolean(getTrailer());

	const onPlay = async () => {
		const orderedGuides = program?.guides
			.filter(guide => !guide.is_trailer)
			.sort((a, b) => (a.position > b.position ? 1 : -1));

		if (!orderedGuides || orderedGuides.length <= 0) return;

		const guideId = orderedGuides[0].id;
		if (onPlayOverride) {
			return await onPlayOverride(guideId);
		}

		await onGuidePlay(guideId);
	};

	const onWatchTrailer = async () => {
		const trailer = getTrailer();

		if (!trailer) return;

		await onGuidePlay(trailer.id);
	};

	return (
		<Wrapper data-testid="alt-program-layout">
			{program?.title && <Title>{program.title}</Title>}
			<Narrator theme="masterclass" imageAlignment="left" program={program} />
			<ButtonWrapper>
				<Button fullWidth={!hasTrailer} backgroundColor="white" textColor="gray8" onPress={onPlay}>
					{formatMessage(messages.playButton)}
				</Button>
				{hasTrailer && (
					<TrailerButton backgroundColor="transparent" onPress={onWatchTrailer}>
						{formatMessage(messages.watchTrailerButton)}
					</TrailerButton>
				)}
			</ButtonWrapper>
			{program?.description && <Description>{program.description}</Description>}
			{program?.guides && hasTrailer && <GuideList program={program} />}
		</Wrapper>
	);
};

export default AltProgramLayout;
